import React, { useState, useEffect } from 'react';
import { adminAPI } from '../../api';
import { Plus, Tag, Trash2, X, Copy, Check } from 'lucide-react';
import toast from 'react-hot-toast';

const EMPTY_FORM = {
  code: '', type: 'percentage', discount_value: '', min_order_amount: '',
  max_discount_amount: '', usage_limit: '', expires_at: '', is_active: true,
};

export default function AdminCoupons() {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [copied, setCopied] = useState(null);

  const fetchCoupons = async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getCoupons();
      setCoupons(res.data.data || []);
    } catch {
      toast.error('Failed to load coupons');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchCoupons(); }, []);

  const generateCode = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';
    const code = Array.from({ length: 8 }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    setForm(f => ({ ...f, code }));
  };

  const handleSave = async () => {
    if (!form.code || !form.discount_value) return toast.error('Code and discount value required');
    setSaving(true);
    try {
      await adminAPI.createCoupon({
        ...form,
        discount_value: parseFloat(form.discount_value),
        min_order_amount: form.min_order_amount ? parseFloat(form.min_order_amount) : 0,
        max_discount_amount: form.max_discount_amount ? parseFloat(form.max_discount_amount) : null,
        usage_limit: form.usage_limit ? parseInt(form.usage_limit) : null,
      });
      toast.success('Coupon created!');
      setShowModal(false);
      setForm(EMPTY_FORM);
      fetchCoupons();
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to create coupon');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    setDeleting(id);
    try {
      await adminAPI.deleteCoupon(id);
      toast.success('Coupon deleted');
      fetchCoupons();
    } catch {
      toast.error('Failed to delete');
    } finally {
      setDeleting(null);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    setCopied(code);
    setTimeout(() => setCopied(null), 2000);
  };

  const now = new Date();

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>Coupons</h1>
          <p style={{ color: '#64748b', marginTop: 2, fontSize: 14 }}>{coupons.length} coupons total</p>
        </div>
        <button onClick={() => setShowModal(true)} style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#f43397', color: 'white', border: 'none', borderRadius: 10, padding: '10px 18px', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
          <Plus size={18} /> Create Coupon
        </button>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}><div className="spinner" /></div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 16 }}>
          {coupons.length === 0 ? (
            <div style={{ gridColumn: '1/-1', background: 'white', borderRadius: 16, padding: 60, textAlign: 'center', color: '#94a3b8', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <Tag size={48} style={{ margin: '0 auto 16px', display: 'block', opacity: 0.3 }} />
              <p style={{ margin: 0 }}>No coupons yet. Create your first coupon!</p>
            </div>
          ) : coupons.map(c => {
            const expired = c.expires_at && new Date(c.expires_at) < now;
            const usagePct = c.usage_limit ? (c.used_count / c.usage_limit) * 100 : null;
            return (
              <div key={c.id} style={{ background: 'white', borderRadius: 16, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', overflow: 'hidden', opacity: expired || !c.is_active ? 0.7 : 1 }}>
                {/* Coupon header */}
                <div style={{ background: 'linear-gradient(135deg, #f43397, #6c3fc5)', padding: '20px 20px 28px', position: 'relative' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12, fontWeight: 600, marginBottom: 4 }}>DISCOUNT CODE</div>
                      <div style={{ color: 'white', fontSize: 24, fontWeight: 800, letterSpacing: 2 }}>{c.code}</div>
                    </div>
                    <div style={{ textAlign: 'right' }}>
                      <div style={{ color: 'white', fontSize: 32, fontWeight: 800 }}>
                        {c.type === 'percentage' ? `${c.discount_value}%` : `₹${c.discount_value}`}
                      </div>
                      <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 12 }}>OFF</div>
                    </div>
                  </div>
                  <div style={{ position: 'absolute', bottom: -1, left: 0, right: 0, height: 20, background: '#f8fafc', borderRadius: '50% 50% 0 0 / 20px 20px 0 0' }} />
                </div>

                {/* Coupon body */}
                <div style={{ padding: '16px 20px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 12 }}>
                    <span>Min. order: <strong style={{ color: '#0f172a' }}>₹{c.min_order_amount || 0}</strong></span>
                    {c.max_discount_amount && <span>Max: <strong style={{ color: '#0f172a' }}>₹{c.max_discount_amount}</strong></span>}
                  </div>

                  {c.usage_limit && (
                    <div style={{ marginBottom: 12 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#64748b', marginBottom: 4 }}>
                        <span>Usage</span><span>{c.used_count || 0}/{c.usage_limit}</span>
                      </div>
                      <div style={{ background: '#f1f5f9', borderRadius: 4, height: 6 }}>
                        <div style={{ background: '#f43397', height: 6, borderRadius: 4, width: `${Math.min(usagePct, 100)}%` }} />
                      </div>
                    </div>
                  )}

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ background: c.is_active && !expired ? '#dcfce7' : '#fee2e2', color: c.is_active && !expired ? '#16a34a' : '#dc2626', padding: '3px 8px', borderRadius: 6, fontSize: 11, fontWeight: 600 }}>
                        {expired ? 'Expired' : c.is_active ? 'Active' : 'Inactive'}
                      </span>
                      {c.expires_at && (
                        <span style={{ background: '#f1f5f9', color: '#64748b', padding: '3px 8px', borderRadius: 6, fontSize: 11 }}>
                          Exp: {new Date(c.expires_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' })}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={() => copyCode(c.code)} style={{ background: '#f1f5f9', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#64748b' }}>
                        {copied === c.code ? <Check size={14} color="#22c55e" /> : <Copy size={14} />}
                      </button>
                      <button onClick={() => handleDelete(c.id)} disabled={deleting === c.id} style={{ background: '#fee2e2', border: 'none', borderRadius: 8, padding: '6px 10px', cursor: 'pointer', color: '#ef4444' }}>
                        {deleting === c.id ? '...' : <Trash2 size={14} />}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: 'white', borderRadius: 20, width: '100%', maxWidth: 480, maxHeight: '90vh', overflow: 'auto' }}>
            <div style={{ padding: '20px 24px', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Create Coupon</h2>
              <button onClick={() => setShowModal(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} color="#64748b" /></button>
            </div>
            <div style={{ padding: 24, display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Coupon Code *</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <input value={form.code} onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))} placeholder="e.g. SAVE20" style={{ flex: 1, padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', fontWeight: 700, letterSpacing: 1 }} />
                  <button onClick={generateCode} style={{ padding: '10px 14px', background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap' }}>Auto Generate</button>
                </div>
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Discount Type</label>
                <div style={{ display: 'flex', gap: 8 }}>
                  {['percentage', 'fixed'].map(t => (
                    <button key={t} onClick={() => setForm(f => ({ ...f, type: t }))} style={{ flex: 1, padding: '10px', border: `2px solid ${form.type === t ? '#f43397' : '#e2e8f0'}`, borderRadius: 10, background: form.type === t ? '#fff0f7' : 'white', color: form.type === t ? '#f43397' : '#64748b', fontWeight: 600, fontSize: 14, cursor: 'pointer', textTransform: 'capitalize' }}>
                      {t === 'percentage' ? '% Percentage' : '₹ Fixed Amount'}
                    </button>
                  ))}
                </div>
              </div>
              {[
                { label: `Discount Value (${form.type === 'percentage' ? '%' : '₹'}) *`, key: 'discount_value' },
                { label: 'Minimum Order Amount (₹)', key: 'min_order_amount' },
                { label: 'Maximum Discount (₹)', key: 'max_discount_amount' },
                { label: 'Usage Limit', key: 'usage_limit' },
              ].map(({ label, key }) => (
                <div key={key}>
                  <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>{label}</label>
                  <input type="number" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
                </div>
              ))}
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 6 }}>Expiry Date</label>
                <input type="date" value={form.expires_at} onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))} style={{ width: '100%', padding: '10px 14px', border: '1px solid #e2e8f0', borderRadius: 10, fontSize: 14, outline: 'none', boxSizing: 'border-box' }} />
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <input type="checkbox" id="coupon_active" checked={form.is_active} onChange={e => setForm(f => ({ ...f, is_active: e.target.checked }))} />
                <label htmlFor="coupon_active" style={{ fontSize: 14 }}>Active immediately</label>
              </div>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end', paddingTop: 8 }}>
                <button onClick={() => setShowModal(false)} style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 10, background: 'white', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
                <button onClick={handleSave} disabled={saving} style={{ padding: '10px 24px', background: '#f43397', color: 'white', border: 'none', borderRadius: 10, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  {saving ? 'Creating...' : 'Create Coupon'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
